import { Box, Divider, HStack, Image, Stack, VStack } from '@chakra-ui/react';

import { CVData, CVSettings, GDPR_CONSENT_TEXT } from '../types';
import { getDisplayPhotoUrl, hexToRgba } from '../utils/cvDefaults';
import { buildContactLinks, filterEmptyEntries, getDuplicateTitles, parseDescription } from '../utils/pdfUtils';

interface TemplateProps {
  data: CVData;
  settings: CVSettings;
}

const FONT_SIZE_MAP = {
  small: { name: '20px', title: '11px', body: '9px', section: '12px', gdpr: '7px' },
  medium: { name: '24px', title: '12px', body: '10px', section: '14px', gdpr: '7.5px' },
  large: { name: '28px', title: '14px', body: '11px', section: '16px', gdpr: '8px' },
};

const CreativeTemplate = ({ data, settings }: TemplateProps) => {
  const { personalInfo, sections } = data;
  const sizes = FONT_SIZE_MAP[settings.fontSize];

  const displayPhotoUrl = getDisplayPhotoUrl(personalInfo.photoUrl, personalInfo.fullName);

  const hasBg = settings.headerBackground;
  const accentBg = hexToRgba(settings.accentColor, settings.accentOpacity ?? 1);

  const contactItems = buildContactLinks(personalInfo);

  return (
    <Box
      width="100%"
      backgroundColor="white"
      color="gray.800"
      fontFamily={settings.fontFamily}
      minHeight="100%"
      overflowWrap="break-word"
      wordBreak="break-word"
    >
      {/* Header */}
      <Box
        backgroundColor={hasBg ? accentBg : 'gray.50'}
        px={8}
        py={6}
        color={hasBg ? 'white' : 'gray.800'}
      >
        <HStack spacing={5} alignItems="center">
          {settings.showPhoto && displayPhotoUrl && (
            <Image
              src={displayPhotoUrl}
              alt={personalInfo.fullName}
              boxSize="70px"
              borderRadius="full"
              objectFit="cover"
              border="3px solid"
              borderColor={hasBg ? 'whiteAlpha.500' : settings.accentColor}
              flexShrink={0}
            />
          )}
          <VStack alignItems="flex-start" spacing={1}>
            <Box fontSize={sizes.name} fontWeight="bold" lineHeight="1.2" color={hasBg ? 'white' : 'gray.800'}>
              {personalInfo.fullName || 'Your Name'}
            </Box>
            <Box fontSize={sizes.title} color={hasBg ? 'whiteAlpha.800' : 'gray.500'} letterSpacing="2px">
              {personalInfo.jobTitle || 'Job Title'}
            </Box>
          </VStack>
        </HStack>
        {contactItems.length > 0 && (
          <HStack
            spacing={0}
            mt={3}
            flexWrap="wrap"
            fontSize={sizes.body}
            color={hasBg ? 'whiteAlpha.800' : 'gray.500'}
          >
            {contactItems.map((item, i) => (
              <HStack key={i} spacing={0}>
                {i > 0 && (
                  <Box as="span" color={hasBg ? 'whiteAlpha.400' : 'gray.300'} mx={2}>|</Box>
                )}
                {item.href ? (
                  <Box as="a" href={item.href} target="_blank" rel="noopener noreferrer" _hover={{ textDecoration: 'underline' }}>
                    {item.display}
                  </Box>
                ) : (
                  <Box as="span">{item.display}</Box>
                )}
              </HStack>
            ))}
          </HStack>
        )}
      </Box>
      {!hasBg && <Divider borderColor={settings.accentColor} opacity={0.3} />}

      <Box px={8} py={6}>
        {/* Ordered content: About + Sections respecting aboutPosition */}
        {(() => {
          const aboutBlock = personalInfo.summary ? (
            <Box key="__about__" mb={5}>
              <Box
                fontSize={sizes.body}
                color="gray.600"
                lineHeight="1.7"
                borderLeft="3px solid"
                borderColor="gray.300"
                pl={4}
                fontStyle="italic"
              >
                {personalInfo.summary}
              </Box>
            </Box>
          ) : null;

          const pos = data.aboutPosition ?? 0;
          const items: React.ReactNode[] = [];
          sections.forEach((section, i) => {
            if (i === pos && aboutBlock) items.push(aboutBlock);
            items.push(
          <Box key={section.id} mb={5}>
            {/* Section header with accent bar */}
            <HStack spacing={3} mb={2} alignItems="center">
              <Box width="4px" height="20px" backgroundColor={settings.accentColor} borderRadius="full" />
              <Box
                fontSize={sizes.section}
                fontWeight="bold"
                color="gray.800"
                letterSpacing="1px"
                flexShrink={0}
                whiteSpace="nowrap"
              >
                {section.title}
              </Box>
              <Divider borderColor={settings.accentColor} opacity={0.3} flex={1} />
            </HStack>

            {section.type === 'skills' ? (() => {
              const filtered = filterEmptyEntries(section.entries);
              const dupes = getDuplicateTitles(filtered);
              const categorized = filtered.reduce<Record<string, string[]>>((acc, entry) => {
                const category = entry.subtitle || 'General';
                if (!acc[category]) acc[category] = [];
                acc[category].push(entry.title);
                return acc;
              }, {});
              const categories = Object.entries(categorized);
              return (
                <Stack spacing={2} pl={4}>
                  {categories.map(([category, skills]) => (
                    <Box key={category}>
                      {categories.length > 1 && category !== 'General' && (
                        <Box fontSize={sizes.body} fontWeight="semibold" color="gray.700" mb={1}>
                          {category}
                        </Box>
                      )}
                      <HStack spacing={2} flexWrap="wrap">
                        {skills.map((skill, i) => {
                          const isDupe = dupes.has(skill.trim().toLowerCase());
                          return (
                            <Box
                              key={i}
                              fontSize={sizes.body}
                              px={3}
                              py={1}
                              backgroundColor={isDupe ? 'red.100' : `${settings.accentColor}15`}
                              border="1px solid"
                              borderColor={isDupe ? 'red.300' : `${settings.accentColor}30`}
                              borderRadius="full"
                              color={isDupe ? 'red.600' : 'gray.700'}
                              mb={1}
                            >
                              {skill}
                            </Box>
                          );
                        })}
                      </HStack>
                    </Box>
                  ))}
                </Stack>
              );
            })() : section.type === 'hobbies' || section.type === 'interests' ? (
              (() => {
                const filtered = filterEmptyEntries(section.entries);
                const dupes = getDuplicateTitles(filtered);
                return (
                  <HStack spacing={2} flexWrap="wrap" pl={4}>
                    {filtered.map((entry) => {
                      const isDupe = dupes.has(entry.title.trim().toLowerCase());
                      return (
                        <Box
                          key={entry.id}
                          fontSize={sizes.body}
                          px={3}
                          py={1}
                          backgroundColor={isDupe ? 'red.100' : `${settings.accentColor}15`}
                          border="1px solid"
                          borderColor={isDupe ? 'red.300' : `${settings.accentColor}30`}
                          borderRadius="full"
                          color={isDupe ? 'red.600' : 'gray.700'}
                          mb={1}
                        >
                          {entry.title}
                        </Box>
                      );
                    })}
                  </HStack>
                );
              })()
            ) : section.type === 'languages' ? (
              <HStack spacing={4} pl={4} flexWrap="wrap">
                {filterEmptyEntries(section.entries).map((entry) => (
                  <VStack key={entry.id} spacing={0} alignItems="center">
                    <Box fontSize={sizes.title} fontWeight="semibold" color="gray.700">
                      {entry.title}
                    </Box>
                    {entry.subtitle && (
                      <Box fontSize={sizes.body} color="gray.400">
                        {entry.subtitle}
                      </Box>
                    )}
                  </VStack>
                ))}
              </HStack>
            ) : section.type === 'certifications' ? (
              <Stack spacing={3} pl={4}>
                {filterEmptyEntries(section.entries).map((entry) => (
                  <Box key={entry.id}>
                    <HStack justifyContent="space-between" alignItems="flex-start">
                      <VStack alignItems="flex-start" spacing={0}>
                        <Box fontSize={sizes.title} fontWeight="bold" color="gray.800">
                          {entry.title}
                        </Box>
                        {entry.subtitle && (
                          <HStack spacing={1} fontSize={sizes.body} color={settings.accentColor}>
                            <span>{entry.subtitle}</span>
                          </HStack>
                        )}
                      </VStack>
                      {(entry.startDate || entry.endDate) && (
                        <Box
                          fontSize={sizes.body}
                          color="gray.400"
                          whiteSpace="nowrap"
                          px={2}
                          py={0.5}
                          backgroundColor="gray.50"
                          borderRadius="sm"
                        >
                          {entry.startDate}
                          {entry.endDate && ` – ${entry.endDate}`}
                        </Box>
                      )}
                    </HStack>
                    {entry.location && (
                      <Box fontSize={sizes.body} color="gray.400" mt={0.5}>
                        ID: {entry.location}
                      </Box>
                    )}
                    {entry.url && (
                      <Box as="a" href={entry.url} fontSize={sizes.body} color={settings.accentColor} textDecoration="underline" mt={0.5} display="block">
                        Verify Credential
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Stack spacing={4} pl={4}>
                {filterEmptyEntries(section.entries).map((entry) => (
                  <Box key={entry.id}>
                    <HStack justifyContent="space-between" alignItems="flex-start">
                      <VStack alignItems="flex-start" spacing={0}>
                        <Box fontSize={sizes.title} fontWeight="bold" color="gray.800">
                          {entry.title}
                        </Box>
                        {entry.subtitle && (
                          <HStack spacing={1} fontSize={sizes.body} color={settings.accentColor}>
                            <span>{entry.subtitle}</span>
                            {entry.location && (
                              <Box as="span" color="gray.400">
                                — {entry.location}
                              </Box>
                            )}
                          </HStack>
                        )}
                      </VStack>
                      {(entry.startDate || entry.endDate) && (
                        <Box
                          fontSize={sizes.body}
                          color="gray.400"
                          whiteSpace="nowrap"
                          px={2}
                          py={0.5}
                          backgroundColor="gray.50"
                          borderRadius="sm"
                        >
                          {entry.startDate}
                          {entry.endDate && ` – ${entry.endDate}`}
                        </Box>
                      )}
                    </HStack>
                    {entry.description && (() => {
                      const segments = parseDescription(entry.description);
                      return (
                        <Box mt={1}>
                          {segments.map((seg, si) => {
                            if (seg.type === 'break') return <Box key={si} height="6px" />;
                            if (seg.type === 'bullet') return (
                              <HStack key={si} spacing={1.5} alignItems="flex-start" mt={0.5} pl={0.5}>
                                <Box as="span" fontSize={sizes.body} color={settings.accentColor} lineHeight="1.6">•</Box>
                                <Box as="span" fontSize={sizes.body} color="gray.600" lineHeight="1.6">{seg.text}</Box>
                              </HStack>
                            );
                            return (
                              <Box key={si} fontSize={sizes.body} color="gray.600" lineHeight="1.6">
                                {seg.text}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })()}
                    {entry.tags.length > 0 && (
                      <HStack spacing={1} mt={1} flexWrap="wrap">
                        {entry.tags.map((tag) => (
                          <Box
                            key={tag}
                            fontSize="8px"
                            px={1.5}
                            py={0.5}
                            backgroundColor={`${settings.accentColor}10`}
                            borderRadius="sm"
                            color={settings.accentColor}
                            fontWeight="medium"
                          >
                            {tag}
                          </Box>
                        ))}
                      </HStack>
                    )}

                    {(entry.url || entry.previewUrl) && (
                      <HStack spacing={3} mt={1} fontSize={sizes.body}>
                        {entry.url && (
                          <Box as="a" href={entry.url} color={settings.accentColor} textDecoration="underline">
                            {entry.urlLabel || 'GitHub'}
                          </Box>
                        )}
                        {entry.previewUrl && (
                          <Box as="a" href={entry.previewUrl} color={settings.accentColor} textDecoration="underline">
                            {entry.previewUrlLabel || 'Live Demo'}
                          </Box>
                        )}
                      </HStack>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
            );
          });
          if (pos >= sections.length && aboutBlock) items.push(aboutBlock);
          return items;
        })()}

        {/* GDPR Consent */}
        {personalInfo.gdprConsent && (
          <Box mt={4} pt={3} borderTop="1px solid" borderColor={`${settings.accentColor}33`}>
            <Box fontSize={sizes.gdpr} color="gray.400" lineHeight="1.5" fontStyle="italic">
              {GDPR_CONSENT_TEXT}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CreativeTemplate;
